import React, {useState, useRef, useEffect} from "react";

import {
    Box,
    Divider,
    TextField,
    Grid,
    Button,
    IconButton,
    Card,
    styled,
    FormHelperText,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ListItemButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import {grey} from "@mui/material/colors";

import {
    Draggable,
    DragDropContext,
    Droppable,
    OnDragEndResponder
} from 'react-beautiful-dnd';

import {useNavigate, useSearchParams} from 'react-router-dom';

import {useFormik} from 'formik';

import {useAppContextFeatures} from "../../../context/AppContext";
import {useAuthAccess} from "../../../hooks/useAuthAccess";

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from "../../common/CommonRestricted";
import DatePickerComponent from "../../common/DatePickerComponent";
import LocationSelector from "../../selector/LocationSelector";

// import SearchCustomers from "../../dialog/SearchCustomers";
// import SearchItems from "../../dialog/SearchItems";
import SearchPickDocuments from "../../dialog/SearchPickDocuments";
import {reOrderArray} from "../../../utils/formatArray";

import {useSystemPrompt} from "../../../hooks/useSystemPrompt";
import {useSystemRoutes, routeTypes} from "../../../hooks/useSystemRoutes";
import {useSystemHead, headTitles} from '../../../hooks/useSystemHead';

import {CommonIcons, icons} from "../../common/CommonIcons";
import {useDocuments, documentTypes, targetTypes} from "../../../hooks/api/useDocuments";
import {usePermissions, scopeIds, scopeTypes, functionTypes} from "../../../hooks/usePermissions";
import {useTranslations} from "../../../hooks/useTranslations";
import {useStatePickOrder} from "../../../hooks/state/loadFrom/documents/useStatePickOrder";

import {validationSchema, initialValues, initialRowValues} from "../../../schema/documents/pickOrderSchema";

import {
    loadFromDocumentTypes,
    LoadFromDocuments
} from "../../dialog/LoadFromDocuments";

const StyledCard = styled(Card)(({theme}) => ({
    padding: theme.spacing(2)
}));

export const StyledIcon = styled(Box)({
    width: 20,
    height: 20,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
});

const styles = {
    draggingListItem: {
        background: 'rgb(235,235,235)'
    },
    flexPaper: {
        flex: 1,
        margin: 16,
        minWidth: 350
    },
}

export default function NewPickOrder({routeType}) {
    const {t} = useTranslations({ns: ["common", "prompt", "@document"]})

    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const {authData, getAvailableLocations} = useAuthAccess();
    const {getLocationIds, setLocationIds} = useAppContextFeatures();

    const {promptMessage} = useSystemPrompt();
    const {getRoute} = useSystemRoutes(routeType)
    const {getHtmlHead} = useSystemHead(routeType)
    const {isPermissionAllow} = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.pickOrder.id)

    const {setSystemLoading} = useAppContextFeatures();
    const {isLoading, create} = useDocuments({
        documentType: documentTypes.pickOrder,
        targetType: targetTypes.pickOrder
    })

    // const [ selectLocationIds, setSelectLocationIds ] = useState()
    const {updateFormikStates, getFormikDependencyStates} = useStatePickOrder()

    const availableLocations = getAvailableLocations();

    const [loadDocumentConfig, setLoadDocumentConfig] = useState({
        show: false,
        documentId: loadFromDocumentTypes.pickOrder.id,
        documentType: loadFromDocumentTypes.pickOrder.type,
        keyword: ""
    })

    const onSubmit = async (currentValues) => {

        const document = {
            doc_date: currentValues.documentDate.format("YYYY-MM-DD"),
            doc_remark: currentValues.remark,
        }

        const props = {
            location_id: currentValues.locationId,
            reference_no: currentValues.referenceNo
        }

        let items = [];

        currentValues.rows.map((item, index) => {
            items.push({
                document_id: item?.id,
                doc_remark: item?.description,
                props: {
                    sequence: (index + 1)
                }
            })
            return false;
        })

        try {

            let data = {
                document,
                props,
                docs: items,
                ...getFormikDependencyStates({values})
            }

            const response = await create({data});

            // console.log('response', response)

            promptMessage(true, `${response?.message}`)

            navigate(getRoute("documents/pickOrders"))

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const {handleSubmit, handleChange, values, errors, touched, setFieldValue,} = formik;

    // useEffect(() => {

    //     calculateSummaryValues();

    // }, [values])

    useEffect(() => {

        setFieldValue(`toLocationId`, ``)
        setFieldValue(`toLocationName`, ``)

    }, [values.locationId])

    const getBreadcumbActionMenus = () => {

        let props = []

        if (isPermissionAllow({functionType: functionTypes.pageNew.id})) {
            props.push({
                displayText: "Load From ...",
                onClick: () => {

                    // check the location is selected - valid
                    if (validateLocationSelection() === false) {
                        promptMessage(false, `Please select any location`)
                        return false;
                    }

                    setLoadDocumentConfig(prev => ({...prev, show: true}))

                    return true;
                }
            })
        }

        return props
    }

    const onDragEnd = ({destination, source}) => {
        // dropped outside the list
        if (!destination) return false;

        const newRows = reOrderArray(values.rows, source.index, destination.index);

        setFieldValue(`rows`, newRows)

        return true;
    };

    const validateLocationSelection = () => {
        // return (selectLocationIds && selectLocationIds.length === 1)
        const selected = getLocationIds()
        return selected && selected.length === 1
    }

    const updateStateFromSearchDocument = (props) => {

        try {

            // check the location is selected - valid
            if (validateLocationSelection() === false) {
                promptMessage(false, `Please select any location`)
                return false;
            }

            updateFormikStates({
                data: props?.data,
                routeType,
                setFieldValue,
            })

        } catch (error) {
            promptMessage(false, `Load failed : ${error}`)
        }

        return true;
    }

    const handleAddItem = () => {

        const currentRows = values?.rows;

        currentRows.push(initialRowValues);
        setFieldValue('rows', currentRows)

    }

    const handleRemoveItem = ({rows, index, setFieldValue}) => {

        const currentLine = rows ? rows.length : 0;

        if (currentLine <= 1) {
            promptMessage(false, `Items at least have (1) row`)
            return false;
        }

        const newRows = rows.filter((i, ind) => ind !== index)

        // rows.remove(index);
        setFieldValue('rows', newRows)

        return true;

    }

    const RenderRow = ({dragId, item, index, key}) => {

        const itemValues = item;
        let itemTouched = touched?.rows
        let itemErrors = errors?.rows
        // const itemTouched = touched?.rows

        if (itemTouched) {
            itemTouched = (itemTouched[index] !== undefined) ? itemTouched[index] : {};
        }

        if (itemErrors) {
            itemErrors = (itemErrors[index] !== undefined) ? itemErrors[index] : {};
        }

        return (
            <Draggable key={key} draggableId={dragId.toString()} index={index}>
                {
                    (provided, snapshot) => {

                        return (
                            <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={snapshot.isDragging ? {backgroundColor: grey[100], borderRadius: 2} : {}}
                            >
                                <Grid container>
                                    <Grid item xs={12} sm={12} md={12}>
                                        <Grid container spacing={0.5}>

                                            <Grid item xs={12} sm={12} md={0.5} alignSelf={"center"}>
                                                <IconButton
                                                    aria-label="delete"
                                                    onClick={() => handleRemoveItem({rows: values.rows, index, setFieldValue})}
                                                >
                                                    <CommonIcons name={icons.remove}/>
                                                </IconButton>
                                            </Grid>

                                            <Grid item xs={12} sm={12} md={3.5}>
                                                <SearchPickDocuments
                                                    routeType={routeType}
                                                    id={`rows.${index}.name`}
                                                    locationId={values?.locationId}
                                                    config={{
                                                        // filterDocumentType: searchDocumentConfig?.documentType,
                                                        // filterKeyword: searchDocumentConfig?.keyword
                                                        dialogDisplayText: `${t('searchPickDocuments')} (${values?.locationName})`
                                                    }}
                                                    // show={searchDocumentConfig?.show}
                                                    handleOnCancel={() => {
                                                        // setOpenSearchDocuments(false)
                                                        // setSearchDocumentConfig(prev => ({...prev, show: false}))
                                                    }}
                                                    handleOnSearchChange={(event) => {
                                                        // setFieldValue(`rows.${index}.name`, `${event.target.value}`)
                                                    }}
                                                    handleOnSelect={({ids, items}) => {

                                                        // console.log('item', item)

                                                        // setFieldValue(`rows.${index}.name`, item.doc_number)
                                                        // setFieldValue(`rows.${index}.description`, item.delivery_profile_name)
                                                        // setFieldValue(`rows.${index}.id`, id)

                                                        // check the documents are selected in list
                                                        if (values?.rows.length > 0) {

                                                            let totalExists = 0

                                                            values?.rows.forEach(i => {
                                                                if (ids.includes(i.id) === true) {
                                                                    totalExists += 1
                                                                }
                                                            })

                                                            // console.log('totalExists, totalExists', totalExists)

                                                            if (totalExists > 0) {
                                                                promptMessage(false, `There are ${totalExists} documents already exists`)
                                                                return false
                                                            }
                                                        }


                                                        // console.log('ids', ids)
                                                        // console.log('items', items)

                                                        // insert the first one
                                                        setFieldValue(`rows.${index}.name`, items[0]?.doc_number)
                                                        setFieldValue(
                                                            `rows.${index}.description`,
                                                            items[0]?.delivery_profile_name
                                                        )
                                                        setFieldValue(`rows.${index}.id`, items[0]?.id)
                                                        setFieldValue(`rows.${index}.documentDocId`, 0)

                                                        // if more than 1
                                                        if (ids.length > 1) {

                                                            // console.log('ids.shift()', ids.shift())
                                                            // console.log('items.shift()', items.shift())

                                                            let newIds = ids.filter((i, ind) => ind !== 0)
                                                            let newItems = items.filter((i, ind) => ind !== 0)

                                                            // console.log('newIds', newIds, 'newItems', newItems)

                                                            // get the last index of the formik state rows
                                                            let currentRowsLength = values?.rows.length
                                                            let startIndex = currentRowsLength

                                                            // console.log('startIndex', startIndex)

                                                            newIds.forEach((i, ind) => {
                                                                setFieldValue(`rows.${startIndex}.name`, newItems[ind]?.doc_number)
                                                                setFieldValue(
                                                                    `rows.${startIndex}.description`,
                                                                    newItems[ind]?.delivery_profile_name
                                                                )
                                                                setFieldValue(`rows.${startIndex}.id`, newItems[ind]?.id)
                                                                setFieldValue(`rows.${startIndex}.documentDocId`, 0)
                                                                startIndex += 1
                                                            })
                                                        }

                                                        return true

                                                    }}
                                                    textProps={{
                                                        disabled: true,
                                                        value: itemValues?.name ? itemValues.name : "",
                                                        error: Boolean(itemErrors?.id),
                                                        helperText: itemErrors?.id
                                                    }}
                                                />

                                            </Grid>

                                            <Grid item xs={12} sm={12} md={7.5}>
                                                <TextField
                                                    margin="dense"
                                                    size="small"
                                                    fullWidth
                                                    name={`rows.${index}.description`}
                                                    label={t("description")}
                                                    type="text"
                                                    variant="outlined"
                                                    value={itemValues?.description ? itemValues.description : ""}
                                                    onChange={handleChange}
                                                    error={Boolean(itemErrors?.description)}
                                                    helperText={itemErrors?.description}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={12} md={0.5} sx={{mt: 2}} display={"flex"} flexDirection={"row"} justifyContent={"center"}
                                                  alignItems={"flex-start"}>
                                                <CommonIcons name={icons.drag}/>
                                            </Grid>

                                        </Grid>
                                    </Grid>
                                </Grid>
                            </ListItem>
                        )
                    }
                }
            </Draggable>
        )
    }

    const RenderRows = () => {

        const rows = values?.rows;

        return (
            <Box
            >
                <List>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="droppable-list">
                            {provided => {
                                return (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                        {
                                            rows && rows.length > 0
                                            && rows.map((item, index) => {

                                                return RenderRow({
                                                    key: index,
                                                    index,
                                                    dragId: index,
                                                    item
                                                })

                                            })
                                        }
                                        {provided.placeholder}
                                    </div>
                                )
                            }}
                        </Droppable>
                    </DragDropContext>
                </List>

                <Grid item xs={12}>
                    <Divider sx={{mt: 1, mb: 0}}/>
                </Grid>

                {
                    RenderSummaryRow()
                }

            </Box>
        )
    }

    const RenderSummaryRow = () => {
        return (
            <Grid item xs={12} sm={12} md={12}>

                <Grid container spacing={0.5}>

                    <Grid item xs={12} sm={12} md={4}>

                        <Button
                            variant="outlined"
                            sx={{mt: 2}}
                            fullWidth
                            startIcon={<CommonIcons name={icons.add}/>}
                            onClick={() => handleAddItem()}
                        >
                            {t('addItem')}
                        </Button>

                    </Grid>

                    <Grid item xs={12} sm={12} md={5}>
                        <></>
                    </Grid>

                </Grid>

            </Grid>
        )
    }

    const RenderFormLocation = () => {

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`location`)}
                            subTitle={t(`relatedWarehouseLocationToProcess`)}
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={10.5}>
                        <TextField
                            disabled
                            margin="dense"
                            size="small"
                            fullWidth
                            name={"locationId"}
                            label={t("fromLocation")}
                            type="text"
                            variant="outlined"
                            value={values.locationName}
                            error={touched.locationId && Boolean(errors.locationId)}
                            helperText={touched.locationId && errors.locationId}
                        />
                    </Grid>
                    <Grid item md={1.5}>
                        <IconButton
                            color="primary"
                            sx={{
                                // pt: 0
                            }}
                            onClick={() => {
                                setLocationIds([])
                            }}
                        >
                            <StyledIcon
                                sx={{
                                    width: 25,
                                    height: 25,
                                    m: 0,
                                    p: 0
                                }}
                            >
                                <CommonIcons name={icons.edit}/>
                            </StyledIcon>
                        </IconButton>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{mt: 2}}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <DatePickerComponent
                                    config={{
                                        name: "documentDate",
                                        label: t("documentDate")
                                    }}
                                    formik={{
                                        values,
                                        errors,
                                        touched,
                                        handleChange,
                                        setFieldValue
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />

                            </Grid>

                            <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"referenceNo"}
                                    label={t("referenceNo")}
                                    type="text"
                                    variant="outlined"
                                    value={values.referenceNo}
                                    onChange={handleChange}
                                    error={touched.referenceNo && Boolean(errors.referenceNo)}
                                    helperText={touched.referenceNo && errors.referenceNo}
                                />

                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                    margin="dense"
                                    size="small"
                                    fullWidth
                                    name={"remark"}
                                    label={t("remark")}
                                    type="text"
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    value={values.remark}
                                    onChange={handleChange}
                                    error={touched.remark && Boolean(errors.remark)}
                                    helperText={touched.remark && errors.remark}
                                />
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderForm = () => {

        return (
            <form onSubmit={handleSubmit}>

                {/* {
                        JSON.stringify(values)
                    } */}

                <Grid container spacing={1}>

                    <Grid item xs={12} sm={12} md={6} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                    </Grid>


                    <Grid item xs={12} sm={12} md={6} textAlign={"left"}>

                        {
                            RenderFormLocation()
                        }

                    </Grid>

                </Grid>
                <StyledCard sx={{mt: 1}}>
                    <Grid container rowSpacing={0}>
                        <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t(`items`)}
                                subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                            />

                        </Grid>

                        <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                            {
                                RenderRows()
                            }

                        </Grid>

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{pl: 2, pr: 2}}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>

            </form>
        )
    }

    const RenderAll = () => {
        return validateLocationSelection() ?
            (
                RenderForm()
            )
            :
            (
                <LocationSelector
                    config={{
                        title: t("selectOnly1Location"),
                        description: t("youHaveToSelectAnyoneOfTheWarehouseLocationToProceed")
                    }}
                    maxSelect={1}
                    handleOnNext={({selected}) => {


                        setLocationIds([selected[0].id])
                        setFieldValue(`locationId`, selected[0].id)
                        setFieldValue(`locationName`, selected[0].location_name)

                    }}
                />
            )
    }

    return (
        <Box>
            {
                getHtmlHead({routeType, title: headTitles.newPickOrder})
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("newPickOrder")}
                actionMenus={
                    getBreadcumbActionMenus()
                }
                breadcrumbs={[
                    {
                        displayText: t("pickOrders"),
                        props: {
                            // underline: "hover",
                            href: getRoute('documents/pickOrders')
                        }
                    },
                    {
                        displayText: t("newPickOrder"),
                        props: {
                            // disabled: true,
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({functionType: functionTypes.pageNew.id}) ?
                    RenderAll()
                    :
                    <CommonRestricted/>
            }

            {
                loadDocumentConfig?.show &&

                <LoadFromDocuments
                    fromDocumentId={loadDocumentConfig?.documentId}
                    fromDocumentType={loadDocumentConfig?.documentType}
                    show={loadDocumentConfig?.show}
                    filledSearchKeyword={loadDocumentConfig?.keyword}
                    handleOnCancel={() => {
                        setLoadDocumentConfig(prev => ({...prev, show: false}))
                    }}
                    handleOnSelect={(props) => {

                        setLoadDocumentConfig(prev => ({...prev, show: false}))

                        // load the data into the state
                        updateStateFromSearchDocument(props)

                    }}
                />
            }

        </Box>
    )
}